import { useEffect, useState } from "react";
import { useAppSelector } from "../../../App/hooks";
import {
  deleteMarketmakingPredefinedProgram,
  getMarketmakingPredefinedPrograms,
  getMarketmakingProgram,
  setMarketmakingPredefinedProgram,
  setMarketmakingProgram,
} from "../../../Requests/deltaHedgeMarketmaking";
import {
  IMarketingProgramRowItem,
  IMarketmakingProgram,
  IMarketmakingPredefinedProgram,
} from "../../../Interfaces/deltaHedgeMarketmaking";
import { packetErrorHandler } from "../../../Utils/requestUtils";
import { toast } from "react-toastify";
import Table from "../../../Components/Table";
import { TableCell, TableRow } from "@mui/material";
import TextInput from "../../../Components/TextInput";
import Button from "../../../Components/Button";
import Modal from "../../../Components/Modal/Modal";
import DeleteIcon from "@mui/icons-material/Delete";
import StaticDropdown from "../../../Components/Dropdown/StaticDropdown";

type ProgramType = "buy" | "sell";

const Program = () => {
  const taskTableClickedRowValues = useAppSelector(
    (store) => store.deltaHedgeMarketmaking.clickedTaskTableRowValues
  );

  const [program, setProgram] = useState({} as IMarketmakingProgram);
  const [isPredefinedModalOpen, setIsPredefinedModalOpen] = useState(false);
  const [predefinedProgramFormValues, setPredefinedProgramFormValues] =
    useState({
      buy: [] as IMarketingProgramRowItem[],
      sell: [] as IMarketingProgramRowItem[],
    } as IMarketmakingPredefinedProgram);
  const [predefinedPrograms, setPredefinedPrograms] = useState(
    [] as IMarketmakingPredefinedProgram[]
  );
  const [selectedPredefinedProgram, setSelectedPredefinedProgram] =
    useState("");
  const [predefinedProgramsRefreshFlag, setPredefinedProgramsRefreshFlag] =
    useState(true);

  useEffect(() => {
    if (!predefinedProgramsRefreshFlag) return;
    getMarketmakingPredefinedPrograms()
      .then((resp) => {
        setPredefinedPrograms(resp);
      })
      .catch(packetErrorHandler)
      .finally(() => setPredefinedProgramsRefreshFlag(false));
  }, [predefinedProgramsRefreshFlag]);

  useEffect(() => {
    if (!taskTableClickedRowValues?.id) return;
    getMarketmakingProgram(taskTableClickedRowValues.id)
      .then((resp) => {
        const buy = Array.from(resp.buy || []);
        const sell = Array.from(resp.sell || []);

        for (let index = 0; index < 5 - (resp.buy?.length || 0); index++) {
          buy.push({
            flexibilityAmount: "" as any,
            flexibilityPercent: "" as any,
            from: "",
            // no: res.sell.length + j,
            to: "",
          } as IMarketingProgramRowItem);
        }
        for (let index = 0; index < 5 - (resp.sell?.length || 0); index++) {
          sell.push({
            flexibilityAmount: "" as any,
            flexibilityPercent: "" as any,
            from: "",
            // no: res.sell.length + j,
            to: "",
          } as IMarketingProgramRowItem);
        }
        setProgram({
          buy,
          sell,
          deviationAmount: resp?.deviationAmount,
          deviationPercent: resp?.deviationPercent,
        });
      })
      .catch((e) => {
        // packetErrorHandler(e);

        const buy = [];
        const sell = [];

        for (let index = 0; index < 5; index++) {
          buy.push({
            flexibilityAmount: "" as any,
            flexibilityPercent: "" as any,
            from: "",
            // no: res.sell.length + j,
            to: "",
          } as IMarketingProgramRowItem);
        }
        for (let index = 0; index < 5; index++) {
          sell.push({
            flexibilityAmount: "" as any,
            flexibilityPercent: "" as any,
            from: "",
            // no: res.sell.length + j,
            to: "",
          } as IMarketingProgramRowItem);
        }
        setProgram({
          buy,
          sell,
          deviationAmount: 0,
          deviationPercent: 0,
        });
      });
  }, [taskTableClickedRowValues?.id]);

  const setProgramHandler = () => {
    if (!taskTableClickedRowValues) return;
    setMarketmakingProgram({
      taskId: taskTableClickedRowValues.id,
      buy: program.buy.filter(
        (b) => b.flexibilityAmount || b.flexibilityPercent || b.from || b.to
      ),
      sell: program.sell.filter(
        (s) => s.flexibilityAmount || s.flexibilityPercent || s.from || s.to
      ),
      deviationAmount: program.deviationAmount,
      deviationPercent: program.deviationPercent,
    })
      .then((resp) => {
        if (resp.data) toast.success(resp.msg);
        else toast.error(resp.msg);
      })
      .catch(packetErrorHandler);
  };

  const setPredefinedProgramHandler = () => {
    if (!taskTableClickedRowValues) return;
    setMarketmakingPredefinedProgram({
      buy: predefinedProgramFormValues.buy.filter(
        (b) => b.flexibilityAmount || b.flexibilityPercent || b.from || b.to
      ),
      sell: predefinedProgramFormValues.sell.filter(
        (s) => s.flexibilityAmount || s.flexibilityPercent || s.from || s.to
      ),
      programName: predefinedProgramFormValues.programName,
      deviationAmount: predefinedProgramFormValues.deviationAmount,
      deviationPercent: predefinedProgramFormValues.deviationPercent,
    })
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          setIsPredefinedModalOpen(false);
          setPredefinedProgramsRefreshFlag(true);
        } else toast.error(resp.msg);
      })
      .catch(packetErrorHandler);
  };

  const changeHandler = (
    name: string,
    value: any,
    index: number,
    type: ProgramType,
    isPredefined?: boolean
  ) => {
    if (isPredefined) {
      const clone = { ...predefinedProgramFormValues };

      clone[type].splice(index, 1, { ...clone[type][index], [name]: value });
      setPredefinedProgramFormValues(clone);
    } else {
      const clone = { ...program };

      clone[type].splice(index, 1, { ...clone[type][index], [name]: value });
      setProgram(clone);
    }
  };

  const commonChangeHandler = (name: string, value: any) => {
    setProgram({ ...program, [name]: value });
  };

  const header = (type: ProgramType) => (
    <>
      <TableCell>ردیف</TableCell>
      <TableCell>حجم {type === "buy" ? "خرید" : "فروش"} ابتدای بازه</TableCell>
      <TableCell>حجم {type === "buy" ? "خرید" : "فروش"} انتهای بازه</TableCell>
      <TableCell>
        قیمت {type === "buy" ? "خرید" : "فروش"} مقدار انعطاف
      </TableCell>
      <TableCell>قیمت {type === "buy" ? "خرید" : "فروش"} درصد انعطاف</TableCell>
    </>
  );

  const body = (
    type: ProgramType,
    values: IMarketmakingProgram,
    isPredefined?: boolean
  ) => (
    <>
      {program && program.buy ? (
        program[type].map((row, i) => (
          <TableRow>
            <TableCell>{i + 1}</TableCell>
            <TableCell>
              <TextInput
                label=""
                name="from"
                onChange={(...params) =>
                  changeHandler(...params, i, type, isPredefined)
                }
                value={values[type][i]?.from}
              />
            </TableCell>
            <TableCell>
              <TextInput
                label=""
                name="to"
                onChange={(...params) =>
                  changeHandler(...params, i, type, isPredefined)
                }
                value={values[type][i]?.to}
              />
            </TableCell>
            <TableCell>
              <TextInput
                label=""
                name="flexibilityAmount"
                onChange={(...params) =>
                  changeHandler(...params, i, type, isPredefined)
                }
                value={values[type][i]?.flexibilityAmount?.toString()}
              />
            </TableCell>
            <TableCell>
              <TextInput
                label=""
                name="flexibilityPercent"
                onChange={(...params) =>
                  changeHandler(...params, i, type, isPredefined)
                }
                value={values[type][i]?.flexibilityPercent?.toString()}
              />
            </TableCell>
          </TableRow>
        ))
      ) : (
        <></>
      )}
    </>
  );

  const getTable = (
    type: ProgramType,
    values: IMarketmakingProgram,
    isPredefined?: boolean
  ) => (
    <div className="w-min m-auto border border-sky-100 rounded overflow-hidden order-table">
      <Table header={header(type)} body={body(type, values, isPredefined)} />
    </div>
  );

  const checkDisabled = () => {
    if (!program) return true;

    const buy = program.buy?.filter(
      (b) =>
        b.flexibilityAmount?.toString() ||
        b.flexibilityPercent?.toString() ||
        b.from?.toString() ||
        b.to?.toString()
    );
    const sell = program.sell?.filter(
      (s) =>
        s.flexibilityAmount?.toString() ||
        s.flexibilityPercent?.toString() ||
        s.from?.toString() ||
        s.to?.toString()
    );
    const filteredBuy = buy?.filter(
      (b) =>
        b?.flexibilityAmount?.toString() &&
        b?.flexibilityPercent?.toString() &&
        b?.from?.toString() &&
        b?.to?.toString()
    );
    const filteredSell = sell?.filter(
      (s) =>
        s?.flexibilityAmount?.toString() &&
        s?.flexibilityPercent?.toString() &&
        s?.from?.toString() &&
        s?.to?.toString()
    );

    if (!filteredBuy?.length || !filteredSell.length) return true;

    if (
      filteredBuy?.length === buy?.length &&
      filteredSell?.length === sell?.length
    )
      return false;
    return true;
  };

  const predefinedChangeHandler = (name: string, value: any) => {
    setPredefinedProgramFormValues({
      ...predefinedProgramFormValues,
      [name]: value,
    });
  };

  const predefinedProgramModalUI = (
    <div className="w-full flex flex-col justify-start items-start gap-5">
      <div className="w-44">
        <TextInput
          label="نام پیشفرض"
          name="programName"
          onChange={predefinedChangeHandler}
          value={predefinedProgramFormValues.programName}
        />
      </div>
      <div className="flex flex-row justify-between items-start w-full gap-5">
        {getTable("buy", predefinedProgramFormValues, true)}
        {getTable("sell", predefinedProgramFormValues, true)}
      </div>
      <div className="flex flex-row justify-start items-center w-full gap-2">
        <div className="text-xs whitespace-nowrap">
          فاصله ردیف اول از میانگین در وضعیت مطلوب
        </div>
        <div className="w-[200px]">
          <TextInput
            label="مقدار اختلاف"
            name="deviationAmount"
            onChange={predefinedChangeHandler}
            value={predefinedProgramFormValues?.deviationAmount?.toString()}
            extraLabel
          />
        </div>
        <div className="w-[200px]">
          <TextInput
            label="درصد اختلاف"
            name="deviationPercent"
            onChange={predefinedChangeHandler}
            value={predefinedProgramFormValues?.deviationPercent?.toString()}
            extraLabel
          />
        </div>
        <div className="mr-auto">
          <Button
            text="ثبت"
            variant="contained"
            onClick={setPredefinedProgramHandler}
            disabled={checkDisabled()}
          />
        </div>
      </div>
    </div>
  );

  const deletePredefinedProgramHandler = (name?: string) => {
    if (!name) return;
    deleteMarketmakingPredefinedProgram(name)
      .then((resp) => {
        if (!resp.data) toast.error(resp.msg);
        else {
          toast.success(resp.msg);
          setPredefinedProgramsRefreshFlag(true);
          setSelectedPredefinedProgram("");
        }
      })
      .catch(packetErrorHandler);
  };

  const onPredefinedChange = (name: string) => {
    setSelectedPredefinedProgram(name);
    const target =
      predefinedPrograms.find((p) => p.programName === name) ||
      ({} as IMarketmakingPredefinedProgram);
    const buy = Array.from(target.buy || []);
    const sell = Array.from(target.sell || []);

    for (let index = 0; index < 5 - (target.buy?.length || 0); index++) {
      buy.push({
        flexibilityAmount: "" as any,
        flexibilityPercent: "" as any,
        from: "",
        // no: res.sell.length + j,
        to: "",
      } as IMarketingProgramRowItem);
    }
    for (let index = 0; index < 5 - (target.sell?.length || 0); index++) {
      sell.push({
        flexibilityAmount: "" as any,
        flexibilityPercent: "" as any,
        from: "",
        // no: res.sell.length + j,
        to: "",
      } as IMarketingProgramRowItem);
    }
    setProgram({
      buy,
      sell,
      deviationAmount: target?.deviationAmount || 0,
      deviationPercent: target?.deviationPercent || 0,
    });
  };

  return (
    <div className="flex flex-col justify-start items-start gap-3 w-full">
      {isPredefinedModalOpen ? (
        <Modal title="برنامه پیش‌فرض"
          UI={predefinedProgramModalUI}
          onClose={() => {
            setIsPredefinedModalOpen(false);
            setPredefinedProgramFormValues({
              buy: [] as IMarketingProgramRowItem[],
              sell: [] as IMarketingProgramRowItem[],
            } as IMarketmakingPredefinedProgram);
          }}
        />
      ) : (
        <></>
      )}
      <div className="flex flex-row justify-between items-start w-full">
        {getTable("buy", program)}
        {getTable("sell", program)}
      </div>
      <div className="flex flex-row w-full justify-start items-center gap-1">
        <div className="text-xs whitespace-nowrap">
          فاصله ردیف اول از میانگین در وضعیت مطلوب
        </div>
        <div className="w-[200px]">
          <TextInput
            label="مقدار اختلاف"
            name="deviationAmount"
            onChange={commonChangeHandler}
            value={program.deviationAmount?.toString()}
            extraLabel
          />
        </div>
        <div className="w-[200px]">
          <TextInput
            label="درصد اختلاف"
            name="deviationPercent"
            onChange={commonChangeHandler}
            value={program.deviationPercent?.toString()}
            extraLabel
          />
        </div>
        <div className="flex flex-row justify-start items-center gap-2 mr-auto">
          <div className="flex flex-row justify-start items-center gap-3">
            {selectedPredefinedProgram ? (
              <div
                onClick={() =>
                  deletePredefinedProgramHandler(selectedPredefinedProgram)
                }
                className="mr-auto"
              >
                <DeleteIcon
                  fontSize="small"
                  className="text-red-700 cursor-pointer"
                />
              </div>
            ) : (
              <></>
            )}
            <StaticDropdown
              dropdown={{ name: "", placeholder: "پیش فرض" }}
              options={predefinedPrograms.map((p) => ({
                id: p.programName,
                name: p.programName,
              }))}
              onChange={(_, v) => onPredefinedChange(v)}
              value={selectedPredefinedProgram}
            />
          </div>
          <Button
            text="ثبت پیشفرض"
            variant="outlined"
            onClick={() => {
              setIsPredefinedModalOpen(true);
              setPredefinedProgramFormValues(
                program as IMarketmakingPredefinedProgram
              );
            }}
            disabled={checkDisabled()}
          />
          <Button
            text="ثبت"
            variant="contained"
            onClick={setProgramHandler}
            disabled={checkDisabled()}
          />
        </div>
      </div>
    </div>
  );
};

export default Program;
